import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { AlertToast } from '../../../../components/AlertToast';
import { AccessibleAutocomplete } from '../../../../components/inputs/AccessibleAutocomplete';
import { StyledInputLabel } from '../../../../components/inputs/InputLabel';
import { ModalDialog } from '../../../../components/ModalDialog';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useAgreements } from '../../../../hooks/api/useAgreements';
import { isApiError } from '../../../../types/Error';
import { StyledQuestionMark } from '../../../../components/QuestionMark';

export const CopyAgreementModal = ({ id }: { id: string }) => {
  const { accountList } = useAuthContext();
  const { copyAgreement } = useAgreements();
  const [open, setOpen] = useState(false);
  const [targetAccount, setTargetAccount] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [description, setDescription] = useState('');
  const [targetAccountError, setTargetAccountError] = useState('');
  const [paymentUrlError, setPaymentUrlError] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const options = useMemo(
    () =>
      Object.entries(accountList as Record<string, string>)
        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
        .map(([name, accountId]) => ({
          label: `${name} - ${accountId}`,
          id: accountId,
        })),
    [accountList]
  );

  const resetFields = () => {
    setTargetAccount(null);
    setPaymentUrl('');
    setDescription('');
    setTargetAccountError('');
    setPaymentUrlError('');
    setShowError(false);
  };

  const handleOpen = () => {
    setOpen(true);
    resetFields();
  };

  const handleClose = () => {
    setOpen(false);
    resetFields();
  };

  const firstMessage = (val: unknown): string => {
    if (Array.isArray(val)) return val[0] ?? '';
    return String(val ?? '');
  };

  const handleConfirm = async () => {
    const newTargetAccountError = !targetAccount
      ? 'Please enter a target account'
      : '';
    const newPaymentUrlError = !paymentUrl.trim()
      ? 'Please enter a payment URL'
      : '';
    setTargetAccountError(newTargetAccountError);
    setPaymentUrlError(newPaymentUrlError);
    if (newTargetAccountError || newPaymentUrlError) return;

    setLoading(true);
    setTargetAccountError('');
    setPaymentUrlError('');
    setShowError(false);
    try {
      const descriptionPayload = description.trim() || undefined;
      await copyAgreement(
        id,
        targetAccount as string,
        paymentUrl.trim(),
        descriptionPayload
      );
      handleClose();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1600);
    } catch (e: unknown) {
      if (isApiError(e)) {
        if (e.payment_url) setPaymentUrlError(firstMessage(e.payment_url));
        if (e.target_account) setTargetAccountError(firstMessage(e.target_account));
        if (!e.payment_url && !e.target_account) {
          setShowError(true);
          setTimeout(() => setShowError(false), 1600);
        }
      } else {
        setShowError(true);
        setTimeout(() => setShowError(false), 1600);
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Button variant="default" onClick={handleOpen}>
        Copy agreement
      </Button>
      <ModalDialog
        open={open}
        onClose={handleClose}
        title="Copy agreement"
        actions={
          <>
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span>Copying</span>
                  <CircularProgress
                    size={16}
                    sx={{ color: 'inherit', ml: 6 }}
                  />
                </>
              ) : (
                'Copy agreement'
              )}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Box>
            <StyledInputLabel htmlFor="copy-target-account">
              Target account <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <AccessibleAutocomplete
              options={options}
              getOptionLabel={(opt) => opt.label}
              onChange={(_, newValue) => setTargetAccount(newValue?.id ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="copy-target-account"
                  error={!!targetAccountError}
                  helperText={targetAccountError}
                />
              )}
            />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledInputLabel htmlFor="copy-payment-url">
                Payment URL <span aria-hidden="true">*</span>
              </StyledInputLabel>
              <Tooltip
                title="Must begin with https://... or be a valid email if 'Contact Library' option is selected"
                placement="top"
              >
                <IconButton size="small" sx={{ marginLeft: '3px' }}>
                  <StyledQuestionMark fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              id="copy-payment-url"
              value={paymentUrl}
              onChange={(e) => setPaymentUrl(e.target.value)}
              fullWidth
              error={!!paymentUrlError}
              helperText={paymentUrlError}
              placeholder="Must begin with https://..."
            />
          </Box>
          <Box>
            <StyledInputLabel htmlFor="copy-description">
              Description (English)
            </StyledInputLabel>
            <TextField
              id="copy-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </Box>
      </ModalDialog>
      {showSuccess && (
        <AlertToast severity="success">
          Agreement copied successfully.
        </AlertToast>
      )}
      {showError && (
        <AlertToast severity="error">
          Something went wrong when copying the agreement. Please try again.
        </AlertToast>
      )}
    </>
  );
};
